export type MediaCategory =
  | 'image'
  | 'video'
  | 'audio'
  | 'document'
  | 'archive'
  | 'code'
  | 'spreadsheet'
  | 'presentation'
  | 'other';

export interface MediaTypeInfo {
  readonly category: MediaCategory;
  readonly extension: string;
  readonly mimeType: string;
  readonly description: string;
  readonly canPreview: boolean;
}

export interface MediaTypeDetectionResult {
  readonly category: MediaCategory;
  readonly extension: string | null;
  readonly mimeType: string | null;
  readonly info: MediaTypeInfo | null;
  readonly canPreview: boolean;
}

/**
 * Comprehensive mapping of file extensions to media type information
 */
const MEDIA_TYPE_REGISTRY = new Map<string, MediaTypeInfo>([
  // Images
  [
    'jpg',
    {
      category: 'image',
      extension: 'jpg',
      mimeType: 'image/jpeg',
      description: 'JPEG Image',
      canPreview: true,
    },
  ],
  [
    'jpeg',
    {
      category: 'image',
      extension: 'jpeg',
      mimeType: 'image/jpeg',
      description: 'JPEG Image',
      canPreview: true,
    },
  ],
  [
    'png',
    {
      category: 'image',
      extension: 'png',
      mimeType: 'image/png',
      description: 'PNG Image',
      canPreview: true,
    },
  ],
  [
    'gif',
    {
      category: 'image',
      extension: 'gif',
      mimeType: 'image/gif',
      description: 'GIF Image',
      canPreview: true,
    },
  ],
  [
    'webp',
    {
      category: 'image',
      extension: 'webp',
      mimeType: 'image/webp',
      description: 'WebP Image',
      canPreview: true,
    },
  ],
  [
    'avif',
    {
      category: 'image',
      extension: 'avif',
      mimeType: 'image/avif',
      description: 'AVIF Image',
      canPreview: true,
    },
  ],
  [
    'svg',
    {
      category: 'image',
      extension: 'svg',
      mimeType: 'image/svg+xml',
      description: 'SVG Vector Image',
      canPreview: true,
    },
  ],
  [
    'bmp',
    {
      category: 'image',
      extension: 'bmp',
      mimeType: 'image/bmp',
      description: 'Bitmap Image',
      canPreview: true,
    },
  ],
  [
    'tiff',
    {
      category: 'image',
      extension: 'tiff',
      mimeType: 'image/tiff',
      description: 'TIFF Image',
      canPreview: true,
    },
  ],
  [
    'tif',
    {
      category: 'image',
      extension: 'tif',
      mimeType: 'image/tiff',
      description: 'TIFF Image',
      canPreview: true,
    },
  ],
  [
    'ico',
    {
      category: 'image',
      extension: 'ico',
      mimeType: 'image/x-icon',
      description: 'Icon File',
      canPreview: true,
    },
  ],
  [
    'heic',
    {
      category: 'image',
      extension: 'heic',
      mimeType: 'image/heic',
      description: 'HEIC Image',
      canPreview: false,
    },
  ],

  // Videos
  [
    'mp4',
    {
      category: 'video',
      extension: 'mp4',
      mimeType: 'video/mp4',
      description: 'MP4 Video',
      canPreview: true,
    },
  ],
  [
    'webm',
    {
      category: 'video',
      extension: 'webm',
      mimeType: 'video/webm',
      description: 'WebM Video',
      canPreview: true,
    },
  ],
  [
    'mov',
    {
      category: 'video',
      extension: 'mov',
      mimeType: 'video/quicktime',
      description: 'QuickTime Video',
      canPreview: true,
    },
  ],
  [
    'avi',
    {
      category: 'video',
      extension: 'avi',
      mimeType: 'video/x-msvideo',
      description: 'AVI Video',
      canPreview: true,
    },
  ],
  [
    'wmv',
    {
      category: 'video',
      extension: 'wmv',
      mimeType: 'video/x-ms-wmv',
      description: 'Windows Media Video',
      canPreview: true,
    },
  ],
  [
    'flv',
    {
      category: 'video',
      extension: 'flv',
      mimeType: 'video/x-flv',
      description: 'Flash Video',
      canPreview: true,
    },
  ],
  [
    'mkv',
    {
      category: 'video',
      extension: 'mkv',
      mimeType: 'video/x-matroska',
      description: 'Matroska Video',
      canPreview: true,
    },
  ],
  [
    'm4v',
    {
      category: 'video',
      extension: 'm4v',
      mimeType: 'video/x-m4v',
      description: 'iTunes Video',
      canPreview: true,
    },
  ],
  [
    '3gp',
    {
      category: 'video',
      extension: '3gp',
      mimeType: 'video/3gpp',
      description: '3GPP Video',
      canPreview: true,
    },
  ],
  [
    'ogv',
    {
      category: 'video',
      extension: 'ogv',
      mimeType: 'video/ogg',
      description: 'Ogg Video',
      canPreview: true,
    },
  ],

  // Audio
  [
    'mp3',
    {
      category: 'audio',
      extension: 'mp3',
      mimeType: 'audio/mpeg',
      description: 'MP3 Audio',
      canPreview: true,
    },
  ],
  [
    'wav',
    {
      category: 'audio',
      extension: 'wav',
      mimeType: 'audio/wav',
      description: 'WAV Audio',
      canPreview: true,
    },
  ],
  [
    'flac',
    {
      category: 'audio',
      extension: 'flac',
      mimeType: 'audio/flac',
      description: 'FLAC Audio',
      canPreview: true,
    },
  ],
  [
    'aac',
    {
      category: 'audio',
      extension: 'aac',
      mimeType: 'audio/aac',
      description: 'AAC Audio',
      canPreview: true,
    },
  ],
  [
    'ogg',
    {
      category: 'audio',
      extension: 'ogg',
      mimeType: 'audio/ogg',
      description: 'Ogg Audio',
      canPreview: true,
    },
  ],
  [
    'm4a',
    {
      category: 'audio',
      extension: 'm4a',
      mimeType: 'audio/mp4',
      description: 'M4A Audio',
      canPreview: true,
    },
  ],
  [
    'wma',
    {
      category: 'audio',
      extension: 'wma',
      mimeType: 'audio/x-ms-wma',
      description: 'Windows Media Audio',
      canPreview: true,
    },
  ],
  [
    'opus',
    {
      category: 'audio',
      extension: 'opus',
      mimeType: 'audio/opus',
      description: 'Opus Audio',
      canPreview: true,
    },
  ],

  // Documents
  [
    'pdf',
    {
      category: 'document',
      extension: 'pdf',
      mimeType: 'application/pdf',
      description: 'PDF Document',
      canPreview: true,
    },
  ],
  [
    'doc',
    {
      category: 'document',
      extension: 'doc',
      mimeType: 'application/msword',
      description: 'Word Document',
      canPreview: false,
    },
  ],
  [
    'docx',
    {
      category: 'document',
      extension: 'docx',
      mimeType:
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      description: 'Word Document',
      canPreview: false,
    },
  ],
  [
    'txt',
    {
      category: 'document',
      extension: 'txt',
      mimeType: 'text/plain',
      description: 'Text File',
      canPreview: true,
    },
  ],
  [
    'rtf',
    {
      category: 'document',
      extension: 'rtf',
      mimeType: 'application/rtf',
      description: 'Rich Text Format',
      canPreview: false,
    },
  ],
  [
    'odt',
    {
      category: 'document',
      extension: 'odt',
      mimeType: 'application/vnd.oasis.opendocument.text',
      description: 'OpenDocument Text',
      canPreview: false,
    },
  ],
  [
    'pages',
    {
      category: 'document',
      extension: 'pages',
      mimeType: 'application/vnd.apple.pages',
      description: 'Apple Pages Document',
      canPreview: false,
    },
  ],

  // Spreadsheets
  [
    'xls',
    {
      category: 'spreadsheet',
      extension: 'xls',
      mimeType: 'application/vnd.ms-excel',
      description: 'Excel Spreadsheet',
      canPreview: false,
    },
  ],
  [
    'xlsx',
    {
      category: 'spreadsheet',
      extension: 'xlsx',
      mimeType:
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      description: 'Excel Spreadsheet',
      canPreview: false,
    },
  ],
  [
    'csv',
    {
      category: 'spreadsheet',
      extension: 'csv',
      mimeType: 'text/csv',
      description: 'CSV File',
      canPreview: true,
    },
  ],
  [
    'ods',
    {
      category: 'spreadsheet',
      extension: 'ods',
      mimeType: 'application/vnd.oasis.opendocument.spreadsheet',
      description: 'OpenDocument Spreadsheet',
      canPreview: false,
    },
  ],
  [
    'numbers',
    {
      category: 'spreadsheet',
      extension: 'numbers',
      mimeType: 'application/vnd.apple.numbers',
      description: 'Apple Numbers Spreadsheet',
      canPreview: false,
    },
  ],

  // Presentations
  [
    'ppt',
    {
      category: 'presentation',
      extension: 'ppt',
      mimeType: 'application/vnd.ms-powerpoint',
      description: 'PowerPoint Presentation',
      canPreview: false,
    },
  ],
  [
    'pptx',
    {
      category: 'presentation',
      extension: 'pptx',
      mimeType:
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      description: 'PowerPoint Presentation',
      canPreview: false,
    },
  ],
  [
    'odp',
    {
      category: 'presentation',
      extension: 'odp',
      mimeType: 'application/vnd.oasis.opendocument.presentation',
      description: 'OpenDocument Presentation',
      canPreview: false,
    },
  ],
  [
    'key',
    {
      category: 'presentation',
      extension: 'key',
      mimeType: 'application/vnd.apple.keynote',
      description: 'Apple Keynote Presentation',
      canPreview: false,
    },
  ],

  // Archives
  [
    'zip',
    {
      category: 'archive',
      extension: 'zip',
      mimeType: 'application/zip',
      description: 'ZIP Archive',
      canPreview: false,
    },
  ],
  [
    'rar',
    {
      category: 'archive',
      extension: 'rar',
      mimeType: 'application/vnd.rar',
      description: 'RAR Archive',
      canPreview: false,
    },
  ],
  [
    '7z',
    {
      category: 'archive',
      extension: '7z',
      mimeType: 'application/x-7z-compressed',
      description: '7-Zip Archive',
      canPreview: false,
    },
  ],
  [
    'tar',
    {
      category: 'archive',
      extension: 'tar',
      mimeType: 'application/x-tar',
      description: 'TAR Archive',
      canPreview: false,
    },
  ],
  [
    'gz',
    {
      category: 'archive',
      extension: 'gz',
      mimeType: 'application/gzip',
      description: 'Gzip Archive',
      canPreview: false,
    },
  ],
  [
    'bz2',
    {
      category: 'archive',
      extension: 'bz2',
      mimeType: 'application/x-bzip2',
      description: 'Bzip2 Archive',
      canPreview: false,
    },
  ],

  // Code files
  [
    'js',
    {
      category: 'code',
      extension: 'js',
      mimeType: 'text/javascript',
      description: 'JavaScript File',
      canPreview: true,
    },
  ],
  [
    'ts',
    {
      category: 'code',
      extension: 'ts',
      mimeType: 'text/typescript',
      description: 'TypeScript File',
      canPreview: true,
    },
  ],
  [
    'jsx',
    {
      category: 'code',
      extension: 'jsx',
      mimeType: 'text/javascript',
      description: 'React JSX File',
      canPreview: true,
    },
  ],
  [
    'tsx',
    {
      category: 'code',
      extension: 'tsx',
      mimeType: 'text/typescript',
      description: 'React TSX File',
      canPreview: true,
    },
  ],
  [
    'html',
    {
      category: 'code',
      extension: 'html',
      mimeType: 'text/html',
      description: 'HTML File',
      canPreview: true,
    },
  ],
  [
    'css',
    {
      category: 'code',
      extension: 'css',
      mimeType: 'text/css',
      description: 'CSS File',
      canPreview: true,
    },
  ],
  [
    'json',
    {
      category: 'code',
      extension: 'json',
      mimeType: 'application/json',
      description: 'JSON File',
      canPreview: true,
    },
  ],
  [
    'xml',
    {
      category: 'code',
      extension: 'xml',
      mimeType: 'application/xml',
      description: 'XML File',
      canPreview: true,
    },
  ],
  [
    'py',
    {
      category: 'code',
      extension: 'py',
      mimeType: 'text/x-python',
      description: 'Python File',
      canPreview: true,
    },
  ],
  [
    'java',
    {
      category: 'code',
      extension: 'java',
      mimeType: 'text/x-java-source',
      description: 'Java File',
      canPreview: true,
    },
  ],
  [
    'cpp',
    {
      category: 'code',
      extension: 'cpp',
      mimeType: 'text/x-c++src',
      description: 'C++ File',
      canPreview: true,
    },
  ],
  [
    'c',
    {
      category: 'code',
      extension: 'c',
      mimeType: 'text/x-csrc',
      description: 'C File',
      canPreview: true,
    },
  ],
  [
    'php',
    {
      category: 'code',
      extension: 'php',
      mimeType: 'text/x-php',
      description: 'PHP File',
      canPreview: true,
    },
  ],
  [
    'rb',
    {
      category: 'code',
      extension: 'rb',
      mimeType: 'text/x-ruby',
      description: 'Ruby File',
      canPreview: true,
    },
  ],
  [
    'go',
    {
      category: 'code',
      extension: 'go',
      mimeType: 'text/x-go',
      description: 'Go File',
      canPreview: true,
    },
  ],
  [
    'rs',
    {
      category: 'code',
      extension: 'rs',
      mimeType: 'text/x-rustsrc',
      description: 'Rust File',
      canPreview: true,
    },
  ],
  [
    'swift',
    {
      category: 'code',
      extension: 'swift',
      mimeType: 'text/x-swift',
      description: 'Swift File',
      canPreview: true,
    },
  ],
  [
    'kotlin',
    {
      category: 'code',
      extension: 'kotlin',
      mimeType: 'text/x-kotlin',
      description: 'Kotlin File',
      canPreview: true,
    },
  ],
  [
    'scala',
    {
      category: 'code',
      extension: 'scala',
      mimeType: 'text/x-scala',
      description: 'Scala File',
      canPreview: true,
    },
  ],
  [
    'sh',
    {
      category: 'code',
      extension: 'sh',
      mimeType: 'text/x-shellscript',
      description: 'Shell Script',
      canPreview: true,
    },
  ],
  [
    'sql',
    {
      category: 'code',
      extension: 'sql',
      mimeType: 'text/x-sql',
      description: 'SQL File',
      canPreview: true,
    },
  ],
  [
    'md',
    {
      category: 'code',
      extension: 'md',
      mimeType: 'text/markdown',
      description: 'Markdown File',
      canPreview: true,
    },
  ],
  [
    'yml',
    {
      category: 'code',
      extension: 'yml',
      mimeType: 'text/yaml',
      description: 'YAML File',
      canPreview: true,
    },
  ],
  [
    'yaml',
    {
      category: 'code',
      extension: 'yaml',
      mimeType: 'text/yaml',
      description: 'YAML File',
      canPreview: true,
    },
  ],
]);

/**
 * MIME type to extension mapping for reverse lookup
 */
const MIME_TO_EXTENSION = new Map<string, string>();
for (const [ext, info] of MEDIA_TYPE_REGISTRY) {
  MIME_TO_EXTENSION.set(info.mimeType, ext);
}

/**
 * Extract file extension from URL or filename
 */
export function extractExtension(input: string): string | null {
  try {
    // Handle URLs
    if (input.startsWith('http://') || input.startsWith('https://')) {
      const url = new URL(input);
      const pathname = url.pathname;
      const extension = pathname.split('.').pop()?.toLowerCase();
      return extension && extension.length > 0 ? extension : null;
    }

    // Handle file paths and filenames
    const extension = input.split('.').pop()?.toLowerCase();
    return extension && extension.length > 0 ? extension : null;
  } catch {
    return null;
  }
}

/**
 * Extract MIME type from file extension
 */
export function getMimeTypeFromExtension(extension: string): string | null {
  const info = MEDIA_TYPE_REGISTRY.get(extension.toLowerCase());
  return info?.mimeType ?? null;
}

/**
 * Extract extension from MIME type
 */
export function getExtensionFromMimeType(mimeType: string): string | null {
  return MIME_TO_EXTENSION.get(mimeType) ?? null;
}

/**
 * Comprehensive media type detection from URL or filename
 */
export function detectMediaType(input: string): MediaTypeDetectionResult {
  const extension = extractExtension(input);
  const info = extension ? (MEDIA_TYPE_REGISTRY.get(extension) ?? null) : null;

  return {
    category: info?.category ?? 'other',
    extension,
    mimeType: info?.mimeType ?? null,
    info,
    canPreview: info?.canPreview ?? false,
  };
}

/**
 * Detect media type from MIME type
 */
export function detectMediaTypeFromMimeType(
  mimeType: string
): MediaTypeDetectionResult {
  const extension = getExtensionFromMimeType(mimeType);
  const info = extension ? (MEDIA_TYPE_REGISTRY.get(extension) ?? null) : null;

  return {
    category: info?.category ?? 'other',
    extension,
    mimeType,
    info,
    canPreview: info?.canPreview ?? false,
  };
}

/**
 * Check if a file type is supported for preview
 */
export function canPreviewFile(input: string): boolean {
  const result = detectMediaType(input);
  return result.canPreview;
}
